import { useEffect, useRef, ReactNode } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { observer } from 'mobx-react-lite'
import LoadingBar, { LoadingBarRef } from 'react-top-loading-bar'

import { AppDebugBar } from '@authy/lib-client/app'
import { Toaster } from '@authy/lib-client/components'

import { DialogStack } from '@/components'
import { useNavigate } from '@/routes'
import { webAppState } from '@/appState'

import { ErrorHandler } from './ErrorHandler'

export type WebAppProps = {
    children?: ReactNode
}

export const WebApp = observer(({ children }: WebAppProps) => {
    const loadingBarRef = useRef<LoadingBarRef | null>(null)
    const loadingTimerId = useRef<ReturnType<typeof setTimeout> | null>(null)

    const navigate = useNavigate()
    const isWaitingRouting = useRouterState({ select: (s) => s.status === 'pending' })

    useEffect(() => {
        if (isWaitingRouting) {
            loadingTimerId.current = setTimeout(() => {
                if (loadingTimerId.current) {
                    loadingTimerId.current = null
                    loadingBarRef.current?.continuousStart(30, 500)
                }
            }, 1000)
        } else {
            if (loadingTimerId.current !== null) {
                clearTimeout(loadingTimerId.current)
                loadingTimerId.current = null
            } else {
                loadingBarRef.current?.complete()
            }
        }
    }, [isWaitingRouting])

    useEffect(() => {
        const errorHandler = (evt: ErrorEvent) => {
            if (evt.error) {
                const message = evt.error.message
                const code = evt.error.data?.code

                // we do this only in production because in dev we want to let the dev server show the error
                if (import.meta.env.PROD) {
                    navigate({
                        replace: true,
                        to: '/error',
                        search: {
                            message,
                            code,
                        },
                    })
                }
            }
        }

        window.addEventListener('error', errorHandler)

        return () => window.removeEventListener('error', errorHandler)
    })

    return (
        <ErrorHandler>
            <LoadingBar className="!bg-success-500" shadow={false} ref={loadingBarRef} height={3} />
            {children}
            <DialogStack />
            {webAppState.isDevelopment && <AppDebugBar />}
            <Toaster />
        </ErrorHandler>
    )
})
